import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Popover, Tooltip } from 'antd';
import { BoldOutlined, ItalicOutlined, LinkOutlined, UnorderedListOutlined } from '@ant-design/icons';

interface Props {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

const styleSelection = (text: string, variant: 'bold' | 'italic'): string =>
  Array.from(text).map((char) => {
    const code = char.codePointAt(0) || 0;
    if (code >= 65 && code <= 90) return String.fromCodePoint((variant === 'bold' ? 0x1d5d4 : 0x1d608) + code - 65);
    if (code >= 97 && code <= 122) return String.fromCodePoint((variant === 'bold' ? 0x1d5ee : 0x1d622) + code - 97);
    if (variant === 'bold' && code >= 48 && code <= 57) return String.fromCodePoint(0x1d7ec + code - 48);
    return char;
  }).join('');

const RichPostEditor: React.FC<Props> = ({ value, onChange, label }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  useEffect(() => {
    if (editorRef.current && value !== lastEmitted.current) {
      editorRef.current.innerText = value || '';
      lastEmitted.current = value || '';
    }
  }, [value]);

  const emit = () => {
    const next = editorRef.current?.innerText.replace(/\r\n/g, '\n') || '';
    lastEmitted.current = next;
    onChange(next);
  };

  const insertText = (text: string) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection) return;
    const range = selection.rangeCount && editor.contains(selection.anchorNode)
      ? selection.getRangeAt(0)
      : document.createRange();
    if (!selection.rangeCount || !editor.contains(selection.anchorNode)) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    editor.focus();
    emit();
  };

  const formatSelection = (variant: 'bold' | 'italic') => {
    const selected = window.getSelection()?.toString() || '';
    if (selected) insertText(styleSelection(selected, variant));
  };

  const insertLink = () => {
    const url = linkUrl.trim();
    if (!/^https?:\/\//i.test(url)) return;
    insertText(url);
    setLinkUrl('');
    setLinkOpen(false);
  };

  return (
    <div style={{ border: '1px solid #3f3f3f', borderRadius: 6, background: '#2a2a2a' }}>
      <div style={{ display: 'flex', gap: 2, padding: 4, borderBottom: '1px solid #3f3f3f' }}>
        <Tooltip title="Bold selected text">
          <Button size="small" type="text" icon={<BoldOutlined />} aria-label="Bold selected text" style={{ color: '#d1d5db' }} onMouseDown={(event) => event.preventDefault()} onClick={() => formatSelection('bold')} />
        </Tooltip>
        <Tooltip title="Italicize selected text">
          <Button size="small" type="text" icon={<ItalicOutlined />} aria-label="Italicize selected text" style={{ color: '#d1d5db' }} onMouseDown={(event) => event.preventDefault()} onClick={() => formatSelection('italic')} />
        </Tooltip>
        <Tooltip title="Insert bullet">
          <Button size="small" type="text" icon={<UnorderedListOutlined />} aria-label="Insert bullet" style={{ color: '#d1d5db' }} onMouseDown={(event) => event.preventDefault()} onClick={() => insertText(`${editorRef.current?.innerText.trim() ? '\n' : ''}• `)} />
        </Tooltip>
        <Popover
          trigger="click"
          open={linkOpen}
          onOpenChange={setLinkOpen}
          content={(
            <div style={{ display: 'flex', gap: 6 }}>
              <Input aria-label="Post link URL" placeholder="https://" value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} onPressEnter={insertLink} style={{ width: 220 }} />
              <Button type="primary" icon={<LinkOutlined />} aria-label="Add post link" onClick={insertLink} />
            </div>
          )}
        >
          <Button size="small" type="text" icon={<LinkOutlined />} aria-label="Insert link" title="Insert link" style={{ color: '#d1d5db' }} />
        </Popover>
      </div>
      <div
        ref={editorRef}
        contentEditable
        role="textbox"
        aria-label={label}
        aria-multiline="true"
        suppressContentEditableWarning
        onInput={emit}
        onPaste={(event) => {
          event.preventDefault();
          insertText(event.clipboardData.getData('text/plain'));
        }}
        style={{ minHeight: 150, maxHeight: 320, overflowY: 'auto', padding: '10px 12px', outline: 'none', color: '#ececec', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}
      />
    </div>
  );
};

export default RichPostEditor;
